import { useEffect, useMemo, useState } from "react";
import { Heart } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const DEFAULT_IMAGES = ["/community/cocina.webp", "/community/madera.webp", "/community/pintura.webp"];
const DEFAULT_MESSAGES = [
  "Crea con las manos, conecta con el corazón. En cada taller hay un espacio esperándote para aprender algo nuevo y conocer personas que se inspiran como tú.",
  "Lo mejor de aprender un oficio no es solo lo que te llevas a casa, sino las risas, las tazas de café y las historias que compartes en la mesa.",
  "Desconéctate de las pantallas y vuelve a lo real. Ven a descubrir un pasatiempo, compartir un buen momento y hacer amigos que comparten tu misma pasión.",
  "Cada taller es un encuentro entre almas creativas. Aprende a tu ritmo, inspírate con la experiencia de un Maestro y siéntete en casa desde el primer minuto.",
  "Aquí no solo aprendes un arte o un oficio: encuentras un lugar donde las conversaciones fluyen, las ideas cobran vida y las personas se conectan de verdad.",
];
const ALT = "Momento en un taller de la comunidad VIVOTaller";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function CommunitySection() {
  const [photos, setPhotos] = useState(null);
  const [messages, setMessages] = useState(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    api.get("/community")
      .then((r) => {
        setPhotos(r.data?.photos?.length ? r.data.photos : DEFAULT_IMAGES);
        setMessages(r.data?.messages?.length ? r.data.messages : DEFAULT_MESSAGES);
      })
      .catch(() => { setPhotos(DEFAULT_IMAGES); setMessages(DEFAULT_MESSAGES); });
  }, []);

  // Orden aleatorio en cada visita; se muestran hasta 3 fotos del conjunto activo.
  const images = useMemo(() => (photos ? shuffle(photos).slice(0, 3) : []), [photos]);
  // Un mensaje por día: estable durante el día, varía de un día a otro.
  const message = useMemo(() => {
    if (!messages || !messages.length) return "";
    return messages[Math.floor(Date.now() / 86400000) % messages.length];
  }, [messages]);

  useEffect(() => {
    if (!images.length) return;
    const t = setTimeout(() => setShown(true), 80);
    return () => clearTimeout(t);
  }, [images.length]);

  if (!images.length) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16" data-testid="community-section">
      <div className="flex items-center justify-center gap-2 text-primary mb-7">
        <Heart className="w-4 h-4 fill-primary" />
        <span className="text-sm font-semibold tracking-[0.15em] uppercase">La comunidad VIVOTaller</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
        {images.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className={`group relative overflow-hidden rounded-2xl bg-muted aspect-[4/3] shadow-[0_14px_44px_-18px_rgba(121,68,228,0.4)] transition-all duration-700 ease-out ${i === 1 ? "sm:-translate-y-5" : ""}`}
            style={{ transitionDelay: `${i * 130}ms`, opacity: shown ? 1 : 0, transform: shown ? undefined : "translateY(28px)" }}
            data-testid={`community-image-${i}`}
          >
            <img
              src={fileUrl(src)}
              alt={ALT}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        ))}
      </div>

      <figure className="mt-11 max-w-3xl mx-auto text-center">
        <span aria-hidden className="font-display text-6xl text-primary/25 leading-none block select-none">&ldquo;</span>
        <blockquote
          key={message}
          className="font-display text-xl sm:text-2xl font-semibold leading-relaxed text-foreground -mt-5 animate-in fade-in slide-in-from-bottom-2 duration-700"
          data-testid="community-message"
        >
          {message}
        </blockquote>
      </figure>
    </section>
  );
}
